import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api/client';
import { GlobalNavigation } from '@/components/global-navigation';
import { LandingFooter } from '@/components/landing/landing-footer';
import { SEOHead } from '@/components/seo/SEOHead';
import { Calendar } from 'lucide-react';

interface ArticleSummary {
  id: string;
  slug: string;
  title: string;
  meta_description: string;
  target_keyword: string;
  published_at: string;
}

export default function BlogListingPage() {
  const { data: articles = [], isLoading } = useQuery({
    queryKey: ['blog-articles'],
    queryFn: () => apiClient.get('/blog/articles?limit=50') as unknown as Promise<ArticleSummary[]>,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <>
      <SEOHead
        title="AI Expert Insights"
        description="Expert guides on hiring AI consultants, implementing AI solutions, and building with the latest AI technologies."
        canonicalUrl="https://aiexpertnetwork.com/blog"
        ogType="website"
      />
      <GlobalNavigation />
      <div className="min-h-screen bg-slate-50">
        <div
          className="py-10 sm:py-14 px-4 sm:px-6 lg:px-8"
          style={{
            background: 'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(248, 250, 252, 1) 100%)',
          }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              AI Expert Insights
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Practical guides on hiring AI talent, implementing AI solutions, and staying ahead with the latest AI technologies.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {isLoading ? (
            <div className="space-y-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 animate-pulse">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-3" />
                  <div className="h-4 bg-slate-200 rounded w-full mb-2" />
                  <div className="h-4 bg-slate-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="space-y-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/blog/${article.slug}`}
                  className="block bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:border-slate-300 transition-all"
                >
                  <h2 className="text-xl font-semibold text-slate-900 mb-2 hover:text-blue-600 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                    {article.meta_description}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Calendar className="h-3 w-3" />
                    <span>{new Date(article.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500">Articles coming soon. Check back shortly.</p>
            </div>
          )}
        </div>
      </div>
      <LandingFooter />
    </>
  );
}
